// 通用工具: 格式化、EXIF、缩略图、ZIP 打包
import { zipSync, type Zippable } from 'fflate';
import { getCachedThumb, cacheThumb } from './db';

export function formatBytes(n: number): string {
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
  if (n < 1024 * 1024 * 1024) return (n / 1024 / 1024).toFixed(1) + ' MB';
  return (n / 1024 / 1024 / 1024).toFixed(2) + ' GB';
}

export function formatDuration(sec?: number): string {
  if (!sec) return '';
  const m = Math.floor(sec / 60), s = Math.floor(sec % 60);
  return m + ':' + String(s).padStart(2, '0');
}

export function isImage(mime: string) { return mime.startsWith('image/'); }
export function isVideo(mime: string) { return mime.startsWith('video/'); }
export function isAudio(mime: string) { return mime.startsWith('audio/'); }
export function isHEIC(mime: string) { return mime.includes('heic') || mime.includes('heif'); }

export function formatDate(ts: number, lang: string): string {
  const d = new Date(ts);
  if (lang === 'zh') return d.toLocaleString('zh-CN');
  return d.toLocaleString('en-US');
}

// 按月分组 key
export function monthKey(ts: number): string {
  const d = new Date(ts);
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
}

// 从 url 取缩略图 blob (优先缓存, 否则 fetch, 再写缓存)
export async function getThumbBlob(url: string, uuid: string): Promise<string> {
  const cached = await getCachedThumb(uuid);
  if (cached) return URL.createObjectURL(cached);
  const r = await fetch(url, { credentials: 'same-origin' });
  if (!r.ok) throw new Error('HTTP ' + r.status);
  const blob = await r.blob();
  cacheThumb(uuid, blob);
  return URL.createObjectURL(blob);
}

// 批量下载: 用 fflate 打包 ZIP, 客户端流式
export async function downloadZip(items: { uuid: string; displayName?: string; url: string }[]): Promise<void> {
  // 逐文件拉 blob
  const files: Zippable = {};
  for (const it of items) {
    const r = await fetch(it.url, { credentials: 'same-origin' });
    if (r.ok) {
      const buf = new Uint8Array(await r.arrayBuffer());
      const name = it.displayName || it.uuid;
      files[name] = buf;
    }
  }
  const zipped = zipSync(files, { level: 0 }); // 无压缩, 快 (图片/视频已压缩)
  const blob = new Blob([zipped], { type: 'application/zip' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'selfie-vault-' + Date.now() + '.zip';
  a.click();
  URL.revokeObjectURL(a.href);
}

// 抽样 SHA-256 (去重用): 首/中/尾各 1MB
export async function sampleHash(file: Blob): Promise<string> {
  const CHUNK = 1024 * 1024;
  const size = file.size;
  const parts: Blob[] = [];
  if (size <= 3 * CHUNK) {
    parts.push(file);
  } else {
    parts.push(file.slice(0, CHUNK));
    parts.push(file.slice(Math.floor(size / 2) - CHUNK / 2, Math.floor(size / 2) + CHUNK / 2));
    parts.push(file.slice(size - CHUNK));
  }
  const merged = new Blob(parts);
  const buf = await merged.arrayBuffer();
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
}

// EXIF 提取 (拍时间/分辨率/相机)
export async function extractExif(file: File): Promise<{ takenAt?: number; width?: number; height?: number }> {
  try {
    // 动态 import, 减小首屏
    const exifr = (await import('exifr')).default;
    const data = await exifr.parse(file, {
      tiff: true, exif: true, gps: false,
      pick: ['DateTimeOriginal', 'ImageWidth', 'ImageHeight', 'ExifImageWidth', 'ExifImageHeight'],
    });
    const takenAt = data?.DateTimeOriginal instanceof Date ? data.DateTimeOriginal.getTime() : undefined;
    const width = data?.ExifImageWidth ?? data?.ImageWidth;
    const height = data?.ExifImageHeight ?? data?.ImageHeight;
    return { takenAt, width, height };
  } catch {
    return { takenAt: undefined };
  }
}

// 视频时长 (metadata)
export function videoDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => { resolve(isFinite(v.duration) ? v.duration : 0); URL.revokeObjectURL(url); };
    v.onerror = () => { resolve(0); URL.revokeObjectURL(url); };
    v.src = url;
  });
}
// 提取媒体宽高(图片/视频) + 视频时长
export async function getMediaMeta(file: File): Promise<{ width?: number; height?: number; duration?: number }> {
  const isImg = file.type.startsWith('image/');
  const isVid = file.type.startsWith('video/');
  if (isImg || isVid) {
    try {
      const url = URL.createObjectURL(file);
      if (isImg) {
        const img = new Image();
        await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = () => rej(new Error('load fail')); img.src = url; });
        URL.revokeObjectURL(url);
        return { width: img.naturalWidth, height: img.naturalHeight };
      }
      // 视频
      const v = document.createElement('video');
      v.preload = 'metadata';
      await new Promise<void>((res, rej) => { v.onloadedmetadata = () => res(); v.onerror = () => rej(new Error('video fail')); v.src = url; });
      const meta = { width: v.videoWidth, height: v.videoHeight, duration: isFinite(v.duration) ? v.duration : undefined };
      URL.revokeObjectURL(url);
      return meta;
    } catch {
      return {};
    }
  }
  return {};
}
// 生成缩略图 blob (图片 canvas压缩 / 视频首帧), 200px 宽
export async function generateThumb(file: Blob, mime: string): Promise<Blob> {
  try {
    const url = URL.createObjectURL(file);
    const isVid = mime.startsWith('video/');
    let srcW = 0, srcH = 0;
    let canvas: HTMLCanvasElement;
    const MAX = 320;

    if (isVid) {
      const v = document.createElement('video');
      v.muted = true; v.playsInline = true; v.preload = 'auto'; v.crossOrigin = 'anonymous';
      await new Promise<void>((res, rej) => {
        v.onloadedmetadata = () => res();
        v.onerror = () => rej(new Error('vid'));
        v.src = url;
        setTimeout(() => res(), 2500); // 兜底: 超时也继续
      });
      // 取帧: seek 到中间, 用 seeked 事件
      await new Promise<void>((res) => {
        const target = Math.min(0.5, (v.duration || 1) / 2);
        let done = false;
        v.onseeked = () => { if (!done) { done = true; res(); } };
        try { v.currentTime = target; } catch {}
        setTimeout(() => { if (!done) { done = true; res(); } }, 2500);
      });
      srcW = v.videoWidth || 1; srcH = v.videoHeight || 1;
      canvas = document.createElement('canvas');
      const scale = Math.min(MAX / srcW, MAX / srcH, 1);
      canvas.width = Math.round(srcW * scale);
      canvas.height = Math.round(srcH * scale);
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    } else {
      const img = new Image();
      await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = () => rej(new Error('img')); img.src = url; });
      srcW = img.naturalWidth; srcH = img.naturalHeight;
      canvas = document.createElement('canvas');
      const scale = Math.min(MAX / srcW, MAX / srcH, 1);
      canvas.width = Math.round(srcW * scale);
      canvas.height = Math.round(srcH * scale);
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    }
    URL.revokeObjectURL(url);
    const blob = await new Promise<Blob>((res) => canvas.toBlob((b) => res(b!), 'image/jpeg', 0.8));
    return blob;
  } catch {
    return file as Blob;
  }
}

// 上传缩略图到后端
export async function uploadThumb(uuid: string, thumb: Blob): Promise<void> {
  try {
    await fetch('/api/thumbs/' + uuid, { method: 'POST', credentials: 'same-origin', body: thumb });
  } catch {}
}

// 获取缩略图 URL (优先后端, 否则生成并上传)
export async function getThumbUrl(uuid: string, file: File | null): Promise<string> {
  const thumbUrl = '/api/thumbs/' + uuid;
  // 先试后端是否有缩略图
  const ok = await fetch(thumbUrl, { method: 'HEAD', credentials: 'same-origin' }).then(r => r.ok).catch(() => false);
  if (ok) return thumbUrl;
  // 生成并上传
  if (file) {
    const thumb = await generateThumb(file, file.type);
    await uploadThumb(uuid, thumb);
    return thumbUrl;
  }
  return thumbUrl;
}